import axios from "../../../../api/axios";

export const INVENTORY_ITEM_ENDPOINTS = {
  GET_PROPERTIES: "v2/master_sheet/property-list",
  BASE: "v2/inventory/item",
  UPLOAD_INVOICE: "v2/inventory/item/upload-invoice",
  GET_INVOICE_SIGNED_URL: "v2/inventory/item/invoice-signed-url",
  MOVE_ITEM: "v2/inventory/item/move",
  GENERATE_REPORT: "v2/inventory/item/generate-report",
  ADD_ITEM_TO_VENDOR: "v2/inventory/item/add-to-vendor",
};

/**
 * Central error handler
 */
const handleApiError = (error, defaultMessage) => {
  const message = error?.response?.data?.message || error.message || defaultMessage;
  throw new Error(message);
};

/**
 * Create new inventory items
 */
export const createInventoryItem = async (payload) => {
  try {
    const response = await axios.post(INVENTORY_ITEM_ENDPOINTS.BASE, payload);
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to create inventory item");
  }
};

/**
 * Update an existing inventory item
 */
export const updateInventoryItem = async (payload) => {
  try {
    const response = await axios.put(INVENTORY_ITEM_ENDPOINTS.BASE, payload);
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to update inventory item");
  }
};

/**
 * Delete an inventory item
 */
export const deleteInventoryItem = async (id) => {
  try {
    const url = `${INVENTORY_ITEM_ENDPOINTS.BASE}/${id}`;
    const response = await axios.delete(url);
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to delete inventory item");
  }
};

/**
 * Fetch all inventory items
 */
export const getAllInventoryItems = async (property_id, building_name) => {
  try {
    const response = await axios.get(INVENTORY_ITEM_ENDPOINTS.BASE, {
      params: { property_id, building_name },
    });
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to fetch inventory items");
  }
};

/**
 * Upload invoice document for an inventory item
 */
export const uploadInvoice = async (property_id, formData, onUploadProgress) => {
  try {
    const url = `${INVENTORY_ITEM_ENDPOINTS.UPLOAD_INVOICE}?property_id=${property_id}`;
    const response = await axios.post(url, formData, {
      onUploadProgress,
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to upload invoice");
  }
};

/**
 * Fetch signed URL for a specific invoice of an inventory item
 */
export const getInvoiceSignedUrl = async (file_url) => {
  try {
    const response = await axios.get(INVENTORY_ITEM_ENDPOINTS.GET_INVOICE_SIGNED_URL, {
      params: { file_url },
    });
    return response.data.data;
  } catch (error) {
    handleApiError(error, "Failed to fetch invoice signed URL");
  }
};

/**
 * Fetches property list based on user role and ID
 * @param {string} role - User role
 * @param {string} id - User ID
 * @returns {Promise<Object>} Filtered property data
 */
export const getPropertyList = async (role, id) => {
  try {
    const { data } = await axios.get(
      `${INVENTORY_ITEM_ENDPOINTS.GET_PROPERTIES}?role=${role || ""}&id=${id || ""}`
    );
    const properties = data.data || [];
    return {
      propertyList: properties,
      building_list: properties.length ? properties[0].building || [] : [],
    };
  } catch (error) {
    handleApiError(error, "Failed to fetch property list");
  }
};

/**
 * Move an inventory item (or specified quantity) to another property
 * @param {Object} payload - The payload containing item_id, destination_property_id, destination_building_name, and optional quantity
 * @returns {Promise<Object>} The response data from the server
 */
export const moveInventoryItem = async (payload) => {
  try {
    const response = await axios.post(INVENTORY_ITEM_ENDPOINTS.MOVE_ITEM, payload);
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to move inventory item");
  }
};

/**
 * Generate an inventory item report
 * @param {Object} payload - The payload containing property_id, building_name, and optional filters (itemName, modelNo, category, location, status)
 * @returns {Promise<Object>} The response data from the server
 */
export const generateInventoryReport = async (payload, config = {}) => {
  try {
    const response = await axios.post(INVENTORY_ITEM_ENDPOINTS.GENERATE_REPORT, payload, {
      ...config,
      responseType: 'blob',
    });
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to generate inventory report");
    throw error;
  }
};

/**
 * Add an inventory item to a specific vendor
 * @param {Object} payload - The payload containing vendor_id and inventory_item_id
 * @returns {Promise<Object>} The response data from the server
 */
export const addItemToVendor = async (payload) => {
  try {
    const response = await axios.post(INVENTORY_ITEM_ENDPOINTS.ADD_ITEM_TO_VENDOR, payload);
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to add item to vendor");
  }
};